<template>

  <div class="d-flex">
    <div class="d-flex flex-column w-75 p-5">

      <div v-if="allUploaded" class="alert alert-success d-flex align-items-center mb-3" role="alert">
        <img class="me-2" :src="require('@/assets/icons/check-circle-green.svg')" width="24" height="24" alt="check" />
        <span>{{ $t('All elements have at least one file uploaded. You can continue to add files or press') }} <strong>{{ $t('Proceed') }}</strong>.</span>
      </div>

      <div class="d-flex justify-content-start p-1">
        <Toggle v-model="shouldUploadModel" :disabled="$props.hasUploadedSomeModels" />
        <label class="ms-2">{{ $t('Enable upload of supplementary files') }}</label>
      </div>

      <!-- Models -->
      <template v-if="shouldUploadModel">
        <div class="text-start">
          <h3 class="d-flex">{{ $t('Select the supplementary files to upload') }}</h3>
          <div class="d-flex flex-wrap">
            <div
              v-for="config in modelConfigs"
              :key="config"
              style="width: 100px; height: 75px;"
              class="text-center my-2">
              <img class="w-100 h-100 cp"
                :title="`${config.name}`"
                :src="require(`@/assets/icons/model/${config.icon}-plus.svg`)"
                @click="$emit('add-model', config.assetType)"
              />
              <span class="text-nowrap">{{ config.name }}</span>
            </div>
          </div>
        </div>
        <div class="d-flex flex-wrap overflow-auto mt-4">
          <RenderAssetIcons :elements="$props.elements" @select="selectModel" :showModels="true" @remove="remove"/>
        </div>
      </template>
      <!-- Projects -->
      <h3 v-if="showProjectsTitle" class="d-flex">Seleziona i progetti da caricare</h3>
      <div class="d-flex flex-wrap overflow-auto">
        <RenderAssetIcons :elements="$props.elements" @select="select" :showProjects="true" @remove="remove"/>
      </div>

      <template v-if="(idsToUpload.length > 0 && ! newBridge)">
        <div v-if="$props.elements.prescription_material?.name" class="mt-4">
          <small>{{ $t('Prescription material') }}: <span class="fw-bold text-uppercase">{{ $props.elements.prescription_material.name }}</span></small>
        </div>
        <div v-if="isDeliveryPhase && hasMapping && mappedLegitMaterial" class="mt-1">
          <small class="text-muted">{{ $t('Material auto-assigned by mapping') }}: <span class="fw-bold text-uppercase">{{ mappedLegitMaterial.name }}</span></small>
        </div>
        <div v-if="requiresMaterialChoice" class="mt-1">
          <small class="text-danger fw-bold">{{ $t('The prescription material is not valid for this phase. Please select a material from the list.') }}</small>
        </div>
        <!-- Materials select for project-->
        <div class="d-flex">
          <div class="input-group mt-4">
          <select id="material-selected" class="form-select" v-model="selectedMaterial.slug" :disabled="isMaterialLocked">
            <option :value="null">Seleziona il materiale</option>
            <option v-for="(material, index) in availableMaterials" :value="material.slug" :key="index">
              {{ material.name }}
            </option>
          </select>
        </div>

        <div v-if="showSelectedFinish" class="input-group mt-4 justify-content-end">
          <span class="me-2">Tipo di finitura: </span>
          <label class="me-2 fw-bold">{{$t('Not matte')}}</label>
            <Toggle v-model="isMatteFinish" />
            <label class="ms-2 fw-bold">{{$t('Matte')}}</label>
          </div>
        </div>
        <!-- Colors select -->
        <div class="input-group mt-4">
          <select id="color-selected" class="form-select" v-model="selectedColorId">
            <option :value="null">Seleziona il colore</option>
            <option v-for="(color, index) in colors" :value="color.id" :key="index">
              {{ color.code }}
            </option>
          </select>
        </div>
      </template>

      <!-- Drag and Drop -->
      <div class="d-flex mt-5">
        <DragAndDropArea
          :title="'Trascina i file per caricare'"
          :subtitle="'oppure'"
          :bridgeStyle="newBridge"
          :upload="idsToUpload"
          :model="modelToUpload"
          :material="selectedMaterial"
          :isMatte="isMatteFinish"
          :colorId="selectedColorId"
          @createBridge="confirmToCreateBridge"
          @assetIdCreated="readAssetIdCreated"
          @resetSelection="resetIdsToUpload"
          @sync-models="syncModels"
        />
      </div>

    </div>

    <div class="d-flex flex-column w-25 bg-light p-2 align-items-center justify-content-between">
      <FilesUploaded
        :data="$props.elements"
        :allUploaded="allUploaded"
        @assetsIdsRemoved="readAssetsIdsRemoved"
      />
    </div>

  </div>

</template>

<script>
import { toRaw, ref, onMounted, watch, inject, reactive, computed } from 'vue';
import _ from "lodash";
import Toggle from '@vueform/toggle';

import DragAndDropArea from "./DragAndDropArea";
import FilesUploaded from "./FilesUploaded";

import { useAssets } from "@/composables/useAssets";
import { index as fetchAllMaterials } from "@/use/repositories/materials/index";
import { fetchAll as fetchAllColors } from "@/use/repositories/colors/fetchAll";
import RenderAssetIcons from '../RenderAssetIcons.vue';

export default {
  name: "Upload",
  components: {
    FilesUploaded,
    DragAndDropArea,
    Toggle,
    RenderAssetIcons
  },
  props: {
    elements: {
      type: Object,
      required: true
    },
    showProjectsTitle: {
      type: Boolean,
      required: true,
    },
    showModelsTitle: {
      type: Boolean,
      required: true,
    },
    hasUploadedSomeModels: {
      type: Boolean,
      default: false,
    }
  },
  emits: [
    'removeBridge',
    'createBridge',
    'asset',
    'assetsIdsRemoved',
    'reset',
    'allFilesUploaded',
    'clear-models-selected',
    'sync-models',
    'add-model',
    'remove-model',
    'force-models-upload',
    'select'

  ],
  setup(props, {emit}) {
    const { findIconByPrescriptionRows } = useAssets();
    const newBridge = ref(false);
    const prepareIdsForBridge = ref([]);
    const idsBridge = ref([]);
    const idsToUpload = ref([]);
    const allMaterials = ref([]);
    const colors = ref([]);
    const selectedMaterial = reactive({
      id: undefined,
      slug: undefined,
    });
    const selectedColorId = ref(null);
    const modelToUpload = ref([]);
    const isModel = ref(false);
    const prefillMaterial = ref(false);
    const prefillColor = ref(false);
    const { materials, modelConfigs, nonDeliveryMillingMaterials, prescriptionTestNames } = inject("constants");
    const modelTypes = ["arch", "gum", "stump", "custom-pin", "gig", "bracket", "bar", "base-arch"];
    const allowedMaterialsForFinish = [materials.ZIRCONIA, materials.RESIN];
    const isMatteFinish = ref(false);

    const isDeliveryPhase = computed(() =>
      props.elements.prescription_test_name?.toLowerCase().includes(prescriptionTestNames.DELIVERY) ?? false
    );

    const isPrescriptionLegit = computed(() => {
      const m = props.elements.prescription_material;
      return m?.legit_material_id != null && m.legit_material_id === m.id;
    });

    const hasMapping = computed(() => {
      const m = props.elements.prescription_material;
      return m?.legit_material_id != null && m.legit_material_id !== m.id;
    });

    const mappedLegitMaterial = computed(() => {
      if (! hasMapping.value) return null;
      const targetId = props.elements.prescription_material.legit_material_id;
      return allMaterials.value.find((m) => m.id === targetId) ?? null;
    });

    const isMaterialLocked = computed(() =>
      isDeliveryPhase.value && (isPrescriptionLegit.value || hasMapping.value)
    );

    const requiresMaterialChoice = computed(() => {
      if (isDeliveryPhase.value) {
        return ! isPrescriptionLegit.value && ! hasMapping.value;
      }
      const slug = props.elements.prescription_material?.slug;
      return ! slug || ! nonDeliveryMillingMaterials.includes(slug);
    });

    const availableMaterials = computed(() => {
      if (! isDeliveryPhase.value) {
        return allMaterials.value.filter((m) => nonDeliveryMillingMaterials.includes(m.slug));
      }

      if (isPrescriptionLegit.value) {
        const slug = props.elements.prescription_material?.slug;
        return allMaterials.value.filter((m) => m.slug === slug);
      }

      if (hasMapping.value) {
        return mappedLegitMaterial.value ? [mappedLegitMaterial.value] : [];
      }

      return allMaterials.value.filter(
        (m) => m.legit_material_id != null && m.legit_material_id === m.id
      );
    });

    const showSelectedFinish = computed(() => {
      return allowedMaterialsForFinish.includes(selectedMaterial.slug);
    });

    const shouldUploadModel = ref(props.elements.models.must_upload);

    const handleMaterial = () => {
      const applySlug = (slug) => {
        const material = availableMaterials.value.find((item) => item.slug === slug);
        if (material) {
          selectedMaterial.id = material.id;
          selectedMaterial.slug = material.slug;
        }
      };

      if (isDeliveryPhase.value) {
        if (isPrescriptionLegit.value) {
          applySlug(props.elements.prescription_material?.slug);
          return;
        }

        if (hasMapping.value) {
          applySlug(mappedLegitMaterial.value?.slug);
          return;
        }

        return;
      }

      const candidate = props.elements.prescription_material?.slug;
      if (candidate && availableMaterials.value.some((m) => m.slug === candidate)) {
        applySlug(candidate);
      }
    };

    watch(() => shouldUploadModel.value, (value) => {
      emit('force-models-upload', value);
    });


    watch(() => selectedMaterial, (value) => {
      const { slug } = value;

      const selectedItem = allMaterials.value.find((item) => item.slug === slug);

      if (selectedItem) {
        selectedMaterial.id = selectedItem.id;
      }
    }, {
      deep: true,
    });

    watch(() => idsToUpload.value.length, (value) => {
      if (value === 0) {
        resetAssetParameters();
      }
    });

    const resetMaterial = () => {
      selectedMaterial.id = props.elements.models?.material?.id ?? null;
      selectedMaterial.slug = props.elements.models?.material?.slug ?? null;
    }

    const resetAssetParameters = () => {
      selectedColorId.value = props.elements.prescription_color_id;
    }

    const prefillAssetParameters = () => {
      selectedColorId.value = props.elements.models.prescription.colorScale.id;
      resetMaterial();
    }

    // Render icons
    const getIcons = (tooth) => {
      // Render only tooth with empty assets
      if (tooth.assets.length > 0) {
        return;
      }

      return findIconByPrescriptionRows(tooth);
    }

    // Read and sent ids to Icon component
    const getIds = (array) => {
      let ids = [];
      array.forEach((el) => {
        ids.push(el.id);
      });
      return ids;
    }

    // Choose icon action.
    const select = (array) => {
      // No cross selected with models
      // Reset models selection if exists
      if (modelToUpload.value.length !== 0) {
        isModel.value = false;
        emit("clear-models-selected");
      }

      // Clear model to upload
      modelToUpload.value = [];

      handleMaterial();

      resetAssetParameters();

      // If elements > 1 is a bridge
      let isBridge = array.length > 1;

      // If elements === 1 is a tooth/arch/sest/emiarch
      let isTooth = array.length === 1;

      if (isBridge) {
        console.log("BRIDGE");
        if (idsToUpload.value.length) {
          resetSelection();
        }

        // Sync ids
        array.forEach((el) => {
          // Toggle ids bridge
          let indexIdsBridge = idsBridge.value.findIndex((item) => item.id === el.id);
          let indexIdsToUpload = idsToUpload.value.findIndex((item) => item === el.id);

          if (indexIdsBridge !== -1) {
            idsBridge.value.splice(indexIdsBridge, 1);
            idsToUpload.value.splice(indexIdsToUpload, 1);
            emit('select', el.id);
          } else {
            emit('select', el.id);
            idsBridge.value.push(el);
            idsToUpload.value.push(el.id);
          }
        });
      }

      if (isTooth) {
        if (idsBridge.value.length > 0) {
          resetSelection();
        }

        // Get tooth
        let tooth = _.head(array);

        console.log("IS TOOTH");

        // A tooth that already has assets must never participate in bridge creation:
        // treat the click as a plain selection for an additional upload.
        const toothHasAssets = tooth.assets?.length > 0;

        if (toothHasAssets) {
          const onlyCurrentInPrepare =
            prepareIdsForBridge.value.length === 0 ||
            (prepareIdsForBridge.value.length === 1 && prepareIdsForBridge.value[0].id === tooth.id);

          if (! onlyCurrentInPrepare) {
            resetSelection();
          }

          newBridge.value = false;
          syncPrepareIdsForBridge(tooth);
          syncIds(idsToUpload, tooth.id);
          return;
        }

        syncPrepareIdsForBridge(tooth);

        // If any of the teeth queued for the bridge already has assets, do not propose a bridge.
        const anyPreparedHasAssets = prepareIdsForBridge.value.some((t) => t.assets?.length > 0);

        if (anyPreparedHasAssets) {
          newBridge.value = false;
          resetSelection();
          syncPrepareIdsForBridge(tooth);
        } else if (prepareIdsForBridge.value.length > 1) {
          if (isPossibleCreateBridge(prepareIdsForBridge.value[prepareIdsForBridge.value.length - 1])) {
            newBridge.value = true;
          } else {
            newBridge.value = false;
            resetSelection();
            syncPrepareIdsForBridge(tooth);
          }
        } else {
          newBridge.value = false;
        }

        syncIds(idsToUpload, tooth.id);
      }
    }

    watch(() => isModel.value, (value) => {
      if (value) {
        // Prefill asset info
        prefillAssetParameters();
      }
    });

    const selectModel = (model) => {
      let selectionRemember = model.selected;

      // Reset all models selected for toggle effect
      emit("clear-models-selected");

      // Reset tooth and bridge selected if exists
      if (idsToUpload.value.length !== 0) {
        resetSelection();
      }

      if (selectionRemember === false) {
        // Prefill and disabled material and color
        prefillMaterial.value = true;
        prefillColor.value = true;
        // Model is selected
        isModel.value = true;
        model.selected = true;
        // Clear model to upload
        modelToUpload.value = [];
        // Prepare model to upload
        modelToUpload.value.push(model);
      }

      if (selectionRemember) {
        model.selected = false;
        // Clear and enabled material and color
        prefillMaterial.value = false;
        prefillColor.value = false;
        // Model not selected
        isModel.value = false;
        // Clear model to upload
        modelToUpload.value = [];
      }
    }

    const syncIds = (array, id) => {
      if (array.value.includes(id)) {
        // Remove tooth ids
        array.value.splice(array.value.indexOf(id), 1);
      } else {
        // Add tooth ids
        array.value.push(id);
      }
    }

    const syncPrepareIdsForBridge = (obj) => {
      // Sync ids tooth
      let index = prepareIdsForBridge.value.findIndex((el) => el.id === obj.id);
      if (index !== -1) {
        obj.selected = false;
        // Remove obj
        prepareIdsForBridge.value.splice(index, 1);
      } else {
        obj.selected = true;
        // Add obj
        prepareIdsForBridge.value.push(obj);
      }
    }

    // Check if possible create a bridge
    const isPossibleCreateBridge = (currentTooth) => {
      // If the last tooth is not sequential
      let lastZoneNumber = parseInt(
        prepareIdsForBridge.value[prepareIdsForBridge.value.length - 2]?.zone_number
      );
      let currentZoneNumber = parseInt(currentTooth.zone_number);

      // Create array with only zone number
      let zoneNumbers = [];
      prepareIdsForBridge.value.forEach((el) => {
        zoneNumbers.push(parseInt(el.zone_number));
      });

      // If the current value is below the threshold
      if (
        (currentZoneNumber === 11 && lastZoneNumber === 21) ||
        (currentZoneNumber === 21 && lastZoneNumber === 11)
      ) {
        return true;
      }

      // If the current value is above the threshold
      if (
        (currentZoneNumber === 31 && lastZoneNumber === 41) ||
        (currentZoneNumber === 41 && lastZoneNumber === 31)
      ) {
        return true;
      }
      // Convert negative value to positive
      return Math.abs(lastZoneNumber - currentZoneNumber) == 1;
    }

    // Read to user choose bridge's action
    const confirmToCreateBridge = (value) => {
      if (value) {
        // Create
        let ids = [];
        prepareIdsForBridge.value.forEach((obj) => ids.push(obj.id));
        emit('createBridge', ids);
      }

      // Remove selected style
      resetSelection();
    }

    const allUploaded = computed(() => {
      const bridgesOk = props.elements.bridges.every(el => _.head(el.tooth)?.assets?.length);

      const teethOk = props.elements.tooth.every(el => el.assets?.length);

      const modelsOk = modelTypes.every(type => {
          const elements = props.elements.models[_.camelCase(type)]?.elements || [];
          return elements.every(el => el.assets?.length);
        });

      const result = bridgesOk && teethOk && modelsOk;

      emit('allFilesUploaded', result);

      return result;
    });


    const resetSelection = () => {
      prepareIdsForBridge.value = [];
      idsToUpload.value = [];
      idsBridge.value = [];
      newBridge.value = false;
      emit('reset');
    }

    const readAssetIdCreated = (obj) => {
      emit('asset', obj);
      resetSelection();
      modelToUpload.value = [];
    }

    const readAssetsIdsRemoved = (payload) => {
      emit('assetsIdsRemoved', payload);
      resetSelection();
    }

    const resetIdsToUpload = (value) => {
      idsToUpload.value = value;
      resetMaterial();
      selectedColorId.value = props.elements.prescription_color_id;
    }

    const syncModels = (obj) => {
      console.log("SYNC MODELS", obj);

      emit("sync-models", obj);
      isModel.value = false;
      emit("clear-models-selected");
    }

    const remove = (event) => {
      if (event.event_type === "remove-model") selectModel(event.element);
      resetSelection();
      return emit(event.event_type, event?.data)
    }

    onMounted(async () => {
      try {
        colors.value = (await fetchAllColors()).colors;
      } catch (error) {
        console.error(error);
      }

      try {
        allMaterials.value = (await fetchAllMaterials()).materials;
      } catch (error) {
        console.error(error);
      }

      handleMaterial();
    });

    return {
      getIcons,
      select,
      toRaw,
      newBridge,
      confirmToCreateBridge,
      prepareIdsForBridge,
      getIds,
      idsToUpload,
      idsBridge,
      readAssetIdCreated,
      readAssetsIdsRemoved,
      resetIdsToUpload,
      allUploaded,
      colors,
      selectedMaterial,
      selectedColorId,
      modelToUpload,
      selectModel,
      prefillMaterial,
      prefillColor,
      syncModels,
      shouldUploadModel,
      availableMaterials,
      isMaterialLocked,
      isDeliveryPhase,
      hasMapping,
      mappedLegitMaterial,
      requiresMaterialChoice,
      remove,
      allowedMaterialsForFinish,
      modelConfigs,
      modelTypes,
      showSelectedFinish,
      isMatteFinish,
    }
  }
}
</script>

<style scoped>
 #stump-plus:hover,
 #gum-plus:hover,
 #custom-pin-plus:hover {
   cursor: pointer;
 }
</style>
